import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Image, Camera, Upload, Trash2, ZoomIn, Download } from "lucide-react";
import { compressImage } from "../../lib/imageUtils";
import { toast } from "sonner";

export const JobDetailPhotosSection = ({
  job,
  isMechanic,
  canEdit,
  checkoutWorkPhotos,
  setCheckoutWorkPhotos,
  onUploadReferencePhoto,
  onDeleteReferencePhoto,
  onEnlargePhoto,
}) => {
  // Handle work photo capture for a specific job type
  const handleWorkPhotoCapture = async (e, jobType) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const maxPhotos = 5;
    const currentPhotos = checkoutWorkPhotos[jobType] || [];
    if (currentPhotos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos allowed per job type`);
      return;
    }

    try {
      const compressedPhotos = await Promise.all(
        files.map((file) => compressImage(file))
      );
      setCheckoutWorkPhotos((prev) => ({
        ...prev,
        [jobType]: [...(prev[jobType] || []), ...compressedPhotos],
      }));
      toast.success(`${files.length} photo(s) added for ${jobType}`);
    } catch (error) {
      toast.error("Failed to capture photos");
    }
  };

  const handleRemoveWorkPhoto = (jobType, index) => {
    setCheckoutWorkPhotos((prev) => ({
      ...prev,
      [jobType]: (prev[jobType] || []).filter((_, i) => i !== index),
    }));
  };

  const jobTypes = job.job_type_tags || [];

  return (
    <>
      {/* Reference Photos */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Image className="w-5 h-5" />
              Reference Photos
            </CardTitle>
            {!isMechanic && canEdit && job.status !== "completed" && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm"
                onClick={() => document.getElementById("ref-photo-upload").click()}
                data-testid="upload-ref-photo-btn"
              >
                <Upload className="w-4 h-4 mr-1" />
                Upload
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <input
            id="ref-photo-upload"
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={onUploadReferencePhoto}
          />
          {(job.photos || []).length === 0 ? (
            <p className="text-slate-400 text-sm">No reference photos</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {job.photos.map((photo, i) => (
                <div key={i} className="relative group">
                  <img
                    src={photo}
                    alt={`Reference ${i + 1}`}
                    className="w-full h-20 object-cover rounded-sm cursor-pointer hover:opacity-80"
                    onClick={() => onEnlargePhoto(photo)}
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded-sm">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-white hover:bg-white/20"
                      onClick={() => onEnlargePhoto(photo)}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                    {!isMechanic && canEdit && job.status !== "completed" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-white hover:bg-red-500/50"
                        onClick={() => onDeleteReferencePhoto(i)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Serial Number Photo (view only) */}
      {job.serial_number_photo && (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Serial Number Photo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <img
              src={job.serial_number_photo}
              alt="Serial number plate"
              className="w-32 h-32 object-cover rounded-sm cursor-pointer hover:opacity-80"
              onClick={() => onEnlargePhoto(job.serial_number_photo)}
            />
          </CardContent>
        </Card>
      )}

      {/* Mechanic Work Photos */}
      {job.check_in && (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Work Photos
              {isMechanic && job.status !== "completed" && (
                <span className="text-red-500 text-sm font-normal">* Required for checkout</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Mechanic: Show upload sections for each job type */}
            {isMechanic && job.status !== "completed" && jobTypes.length > 0 && (
              <div className="space-y-4">
                {jobTypes.map((jobType) => (
                  <div key={jobType} className="border rounded-sm p-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-semibold text-slate-700">
                        {jobType}
                        <span className="text-red-500 ml-1">*</span>
                        <span className="text-xs text-slate-400 ml-2">
                          ({(checkoutWorkPhotos[jobType] || []).length}/5 photos)
                        </span>
                      </Label>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm"
                        onClick={() => document.getElementById(`work-photo-${jobType}`).click()}
                      >
                        <Camera className="w-4 h-4 mr-1" />
                        Add Photos
                      </Button>
                    </div>
                    <input
                      id={`work-photo-${jobType}`}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      className="hidden"
                      onChange={(e) => handleWorkPhotoCapture(e, jobType)}
                    />
                    {(checkoutWorkPhotos[jobType] || []).length === 0 ? (
                      <p className="text-xs text-amber-600">At least 1 photo required</p>
                    ) : (
                      <div className="grid grid-cols-4 gap-2">
                        {checkoutWorkPhotos[jobType].map((photo, i) => (
                          <div key={i} className="relative group">
                            <img
                              src={photo}
                              alt={`${jobType} ${i + 1}`}
                              className="w-full h-16 object-cover rounded-sm"
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="absolute top-0 right-0 h-5 w-5 bg-red-500/80 hover:bg-red-600 text-white rounded-full"
                              onClick={() => handleRemoveWorkPhoto(jobType, i)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Generic upload for jobs without types */}
            {isMechanic && job.status !== "completed" && jobTypes.length === 0 && (
              <div className="border rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold text-slate-700">
                    Work Photos
                    <span className="text-red-500 ml-1">*</span>
                    <span className="text-xs text-slate-400 ml-2">
                      ({(checkoutWorkPhotos["general"] || []).length}/5 photos)
                    </span>
                  </Label>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => document.getElementById("work-photo-general").click()}
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    Add Photos
                  </Button>
                </div>
                <input
                  id="work-photo-general"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="hidden"
                  onChange={(e) => handleWorkPhotoCapture(e, "general")}
                />
                {(checkoutWorkPhotos["general"] || []).length === 0 ? (
                  <p className="text-xs text-amber-600">At least 1 photo required</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {checkoutWorkPhotos["general"].map((photo, i) => (
                      <div key={i} className="relative group">
                        <img
                          src={photo}
                          alt={`Work ${i + 1}`}
                          className="w-full h-16 object-cover rounded-sm"
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="absolute top-0 right-0 h-5 w-5 bg-red-500/80 hover:bg-red-600 text-white rounded-full"
                          onClick={() => handleRemoveWorkPhoto("general", i)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Show saved work photos for viewing */}
            {(job.mechanic_work_photos || []).length > 0 && (
              <div>
                <Label className="text-xs text-slate-500 uppercase font-semibold mb-2 block">
                  Saved Work Photos
                </Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {job.mechanic_work_photos.map((photo, i) => (
                    <img
                      key={i}
                      src={photo}
                      alt={`Work ${i + 1}`}
                      className="w-full h-20 object-cover rounded-sm cursor-pointer hover:opacity-80"
                      onClick={() => onEnlargePhoto(photo)}
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default JobDetailPhotosSection;
